import React, { useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { Line2, LineGeometry, LineMaterial } from "three-stdlib";

export interface CameraWaypoint {
  position: [number, number, number];
  lookAt: [number, number, number];
  zoom?: number;
}

interface CitySceneProps {
  progressRef: React.MutableRefObject<number>;
  waypoints: CameraWaypoint[];
  breakpoints: number[];
  routeColorStops: string[];
}

const findSegment = (breakpoints: number[], p: number) => {
  for (let i = 0; i < breakpoints.length - 2; i++) {
    if (p < breakpoints[i + 1]) return i;
  }
  return breakpoints.length - 2;
};

const rand = (seed: number) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

/* ------------------------------------------------------------------ */
/* Palette                                                             */
/* ------------------------------------------------------------------ */

const SHELL = "#f4f7fc";
const SHELL_DIM = "#e3eaf5";
const ROOF = "#d7e0ee";
const TRIM = "#c2cee3";
const GLASS = "#9fc3ff";
const WATER = "#b9d4ff";
const ASPHALT = "#dbe3f0";
const PLATE = "#edf2fb";
const PLATE_EDGE = "#d3ddef";
const LAWN = "#d5edea";
const FOLIAGE = "#bfe4dc";
const FOLIAGE_DIM = "#a9d8cf";
const OUTLINE = "#aebcd8";
const SOLAR = "#2c47c9";

const ACCENTS = ["#4c5cf0", "#3edcff", "#9d8cff", "#0b1c75", "#6f7dff"];

/* ------------------------------------------------------------------ */
/* Procedural city blocks                                              */
/* ------------------------------------------------------------------ */

type BuildingKind = "tower" | "warehouse" | "house" | "block";

interface Building {
  position: [number, number, number];
  size: [number, number, number];
  roofInset?: boolean;
  kind: BuildingKind;
  seed: number;
}

const classifyBuilding = (w: number, h: number, d: number): BuildingKind => {
  if (h > 4.5) return "tower";
  if (w * d > 3.4 && h < 3) return "warehouse";
  if (w < 2 && d < 2 && h < 2.4) return "house";
  return "block";
};

/**
 * Height of the raised district plate each cluster stands on. Buildings, trees
 * and lamps sit on top of it; roads are drawn above it so junctions still read.
 * Declared here because generateCluster below runs at module load.
 */
const PLATE_TOP = 0.14;

const generateCluster = (
  centerX: number,
  centerZ: number,
  count: number,
  seedOffset: number
): Building[] => {
  const buildings: Building[] = [];
  for (let i = 0; i < count; i++) {
    const s = seedOffset + i * 7.31;
    const w = 1.4 + rand(s) * 1.6;
    const d = 1.4 + rand(s + 1) * 1.6;
    const h = 1.5 + rand(s + 2) * 5.5;
    // Kept inside DISTRICT_HALF so every building lands on its plate.
    const ox = (rand(s + 3) - 0.5) * 6;
    const oz = (rand(s + 4) - 0.5) * 6;
    buildings.push({
      position: [centerX + ox, PLATE_TOP + h / 2, centerZ + oz],
      size: [w, h, d],
      roofInset: rand(s + 5) > 0.4,
      kind: classifyBuilding(w, h, d),
      seed: s
    });
  }
  return buildings;
};

const scatterPoints = (
  centerX: number,
  centerZ: number,
  count: number,
  radius: number,
  seedOffset: number
): Array<[number, number]> => {
  const points: Array<[number, number]> = [];
  for (let i = 0; i < count; i++) {
    const s = seedOffset + i * 5.17;
    const angle = rand(s) * Math.PI * 2;
    const r = radius * (0.4 + rand(s + 1) * 0.6);
    points.push([centerX + Math.cos(angle) * r, centerZ + Math.sin(angle) * r]);
  }
  return points;
};

// City blocks sit inside the grid formed by the delivery road, so nothing
// is built on top of the route the truck drives. Each centre also carries a
// raised district plate, so they're spaced to keep those plates clear of the
// roads — see DISTRICT_HALF.
const CLUSTER_CENTERS: Array<[number, number]> = [
  [-11, -12],
  [-11, 16],
  [6, -8],
  [8, 2],
  [25, 14],
  [26, -6],
  [41, 4]
];

/** Half-width of the plate each cluster sits on. */
const DISTRICT_HALF = 5;

const BUILDING_CLUSTERS: Building[] = CLUSTER_CENTERS.flatMap(([cx, cz], i) =>
  generateCluster(cx, cz, 5 + (i % 3), 11 + i * 30)
);

// Radii stay inside DISTRICT_HALF so nothing hovers off the edge of a plate.
const TREES: Array<[number, number]> = CLUSTER_CENTERS.flatMap(([cx, cz], i) =>
  scatterPoints(cx, cz, 6, 4.3, 400 + i * 37)
);

const LAMPS: Array<[number, number]> = CLUSTER_CENTERS.flatMap(([cx, cz], i) =>
  scatterPoints(cx, cz, 2, 4.6, 700 + i * 53)
);

const BENCHES: Array<[number, number]> = CLUSTER_CENTERS.flatMap(([cx, cz], i) =>
  scatterPoints(cx, cz, 1, 4.0, 900 + i * 61)
);

const TURBINES: Array<[number, number]> = [
  [22, -22],
  [26, -20],
  [30, -23],
  [19, -19]
];

/* ------------------------------------------------------------------ */
/* Shared materials / textures                                         */
/* ------------------------------------------------------------------ */

const createWindowTileTexture = () => {
  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.fillStyle = "#eef2f9";
    ctx.fillRect(0, 0, 64, 64);
    ctx.fillStyle = GLASS;
    ctx.fillRect(10, 8, 44, 40);
    ctx.fillStyle = "rgba(255,255,255,0.55)";
    ctx.fillRect(10, 8, 44, 8);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
};

const useWindowTexture = (w: number, h: number) =>
  useMemo(() => {
    const texture = createWindowTileTexture();
    texture.repeat.set(Math.max(1, Math.round(w * 1.4)), Math.max(1, Math.round(h * 1.1)));
    return texture;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w, h]);

/* ------------------------------------------------------------------ */
/* Buildings                                                           */
/* ------------------------------------------------------------------ */

const TowerBuilding = ({ b }: { b: Building }) => {
  const [w, h, d] = b.size;
  const sideTexture = useWindowTexture(w, h);
  const frontTexture = useWindowTexture(d, h);

  return (
    <group position={b.position}>
      <mesh castShadow receiveShadow>
        <boxGeometry args={b.size} />
        <meshStandardMaterial attach="material-0" map={frontTexture} roughness={0.75} />
        <meshStandardMaterial attach="material-1" map={frontTexture} roughness={0.75} />
        <meshStandardMaterial attach="material-2" color="#eef2f9" roughness={0.8} />
        <meshStandardMaterial attach="material-3" color={SHELL_DIM} roughness={0.9} />
        <meshStandardMaterial attach="material-4" map={sideTexture} roughness={0.75} />
        <meshStandardMaterial attach="material-5" map={sideTexture} roughness={0.75} />
      </mesh>
      <mesh position={[0, h / 2 + 0.05, 0]}>
        <boxGeometry args={[w * 0.94, 0.12, d * 0.94]} />
        <meshStandardMaterial color={ROOF} roughness={0.9} />
      </mesh>
      {rand(b.seed + 6) > 0.5 && (
        <mesh position={[w * 0.15, h / 2 + 0.34, d * 0.1]}>
          <boxGeometry args={[0.34, 0.55, 0.34]} />
          <meshStandardMaterial color={TRIM} roughness={0.8} />
        </mesh>
      )}
      {rand(b.seed + 8) > 0.6 && (
        <mesh position={[-w * 0.2, h / 2 + 0.26, -d * 0.18]}>
          <cylinderGeometry args={[0.16, 0.16, 0.4, 10]} />
          <meshStandardMaterial color={ROOF} roughness={0.85} />
        </mesh>
      )}
    </group>
  );
};

const WarehouseBuilding = ({ b }: { b: Building }) => {
  const [w, h, d] = b.size;
  const striped = rand(b.seed + 7) > 0.5;

  return (
    <group position={b.position}>
      <mesh castShadow receiveShadow>
        <boxGeometry args={b.size} />
        <meshStandardMaterial color={SHELL} roughness={0.85} metalness={0.02} />
      </mesh>
      <mesh position={[0, -h * 0.18, d / 2 + 0.01]}>
        <planeGeometry args={[w * 0.5, h * 0.5]} />
        <meshStandardMaterial color={TRIM} roughness={0.7} />
      </mesh>
      {striped ? (
        [-2, -1, 0, 1, 2].map((offset) => (
          <mesh key={offset} position={[offset * w * 0.17, h / 2 + 0.05, 0]}>
            <boxGeometry args={[w * 0.1, 0.1, d * 0.85]} />
            <meshStandardMaterial color="#bcd6ff" roughness={0.4} transparent opacity={0.75} />
          </mesh>
        ))
      ) : (
        <mesh position={[0, h / 2 + 0.06, 0]}>
          <boxGeometry args={[w * 0.94, 0.12, d * 0.22]} />
          <meshStandardMaterial color="#bcd6ff" roughness={0.4} transparent opacity={0.75} />
        </mesh>
      )}
      {[-1, 0, 1].map((offset) => (
        <mesh key={offset} position={[offset * w * 0.24, h / 2 + 0.16, -d * 0.22]}>
          <boxGeometry args={[0.3, 0.22, 0.3]} />
          <meshStandardMaterial color={ROOF} roughness={0.9} />
        </mesh>
      ))}
    </group>
  );
};

const HouseBuilding = ({ b }: { b: Building }) => {
  const [w, h, d] = b.size;
  return (
    <group position={b.position}>
      <mesh castShadow receiveShadow>
        <boxGeometry args={b.size} />
        <meshStandardMaterial color="#f6f8fc" roughness={0.85} />
      </mesh>
      <mesh position={[0, h / 2 + d * 0.22, 0]} rotation={[0, Math.PI / 4, 0]} castShadow>
        <coneGeometry args={[w * 0.78, d * 0.5, 4]} />
        <meshStandardMaterial color="#c9d6ec" roughness={0.8} />
      </mesh>
      <mesh position={[w * 0.28, h / 2 + d * 0.5, -d * 0.1]}>
        <boxGeometry args={[0.14, 0.32, 0.14]} />
        <meshStandardMaterial color="#b7c3da" roughness={0.9} />
      </mesh>
    </group>
  );
};

const BlockBuilding = ({ b }: { b: Building }) => (
  <group position={b.position}>
    <mesh castShadow receiveShadow>
      <boxGeometry args={b.size} />
      <meshStandardMaterial color={SHELL} roughness={0.85} metalness={0.02} />
    </mesh>
    {b.roofInset && (
      <mesh position={[0, b.size[1] / 2 + 0.001, 0]}>
        <boxGeometry args={[b.size[0] * 0.45, 0.15, b.size[2] * 0.45]} />
        <meshStandardMaterial color={ROOF} roughness={0.9} />
      </mesh>
    )}
  </group>
);

const BuildingMesh = ({ b }: { b: Building }) => {
  const [, h] = b.size;

  const body = (() => {
    switch (b.kind) {
      case "tower":
        return <TowerBuilding b={b} />;
      case "warehouse":
        return <WarehouseBuilding b={b} />;
      case "house":
        return <HouseBuilding b={b} />;
      default:
        return <BlockBuilding b={b} />;
    }
  })();

  // Flat roofs get a solar array; pitched ones and towers (already busy with
  // rooftop plant) don't.
  const solar = (b.kind === "block" || b.kind === "warehouse") && rand(b.seed + 12) > 0.45;
  const solarY = b.position[1] + h / 2 + (b.kind === "warehouse" ? 0.22 : 0.08);

  return (
    <>
      {body}
      <BoxOutline size={b.size} position={b.position} />
      {solar && <SolarArray x={b.position[0]} y={solarY} z={b.position[2]} />}
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Street furniture                                                    */
/* ------------------------------------------------------------------ */

/**
 * Faceted low-poly canopy rather than a smooth sphere — the flat shading is
 * what reads as "low poly" at this scale. Every third tree is a conifer so the
 * plantings aren't uniform.
 */
const Tree = ({ x, z, scale = 1, seed = 0 }: { x: number; z: number; scale?: number; seed?: number }) => {
  const conifer = rand(seed + 3.7) > 0.68;

  return (
    <group position={[x, 0, z]} scale={scale}>
      <mesh position={[0, 0.3, 0]} castShadow>
        <cylinderGeometry args={[0.05, 0.07, 0.6, 6]} />
        <meshStandardMaterial color="#b9c6dd" roughness={0.85} />
      </mesh>

      {conifer ? (
        <>
          <mesh position={[0, 0.95, 0]} castShadow>
            <coneGeometry args={[0.42, 1.15, 6]} />
            <meshStandardMaterial color={FOLIAGE} roughness={0.8} flatShading />
          </mesh>
          <mesh position={[0, 1.42, 0]} castShadow>
            <coneGeometry args={[0.27, 0.66, 6]} />
            <meshStandardMaterial color={FOLIAGE_DIM} roughness={0.8} flatShading />
          </mesh>
        </>
      ) : (
        <>
          <mesh position={[0, 0.82, 0]} rotation={[0.3, rand(seed) * 2, 0]} castShadow>
            <icosahedronGeometry args={[0.46, 0]} />
            <meshStandardMaterial color={FOLIAGE} roughness={0.8} flatShading />
          </mesh>
          <mesh position={[0.2, 1.04, 0.12]} rotation={[0.5, rand(seed + 1) * 2, 0]} castShadow>
            <icosahedronGeometry args={[0.28, 0] } />
            <meshStandardMaterial color={FOLIAGE_DIM} roughness={0.8} flatShading />
          </mesh>
        </>
      )}
    </group>
  );
};

/* ------------------------------------------------------------------ */
/* District plates, outlines and rooftop kit                           */
/* ------------------------------------------------------------------ */

const DistrictPlate =({ x, z, seed }: { x: number; z: number; seed: number }) => {
  const size = DISTRICT_HALF * 2;
  const lawn = size - 1.6 - rand(seed) * 1.2;

  return (
    <group position={[x, 0, z]}>
      <mesh position={[0, PLATE_TOP / 2, 0]} receiveShadow castShadow>
        <boxGeometry args={[size, PLATE_TOP, size]} />
        <meshStandardMaterial color={PLATE_EDGE} roughness={1} />
      </mesh>
      {/* inset top face, slightly smaller so the rim shows as a bevel */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, PLATE_TOP + 0.002, 0]} receiveShadow>
        <planeGeometry args={[size - 0.5, size - 0.5]} />
        <meshStandardMaterial color={PLATE} roughness={1} />
      </mesh>
      {/* lawn */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[(rand(seed + 1) - 0.5) * 1.6, PLATE_TOP + 0.006, (rand(seed + 2) - 0.5) * 1.6]}
        receiveShadow
      >
        <planeGeometry args={[lawn, lawn]} />
        <meshStandardMaterial color={LAWN} roughness={1} />
      </mesh>
    </group>
  );
};

/** Edge stroke around a box — the line-art outline from the reference sheet. */
const BoxOutline = ({
  size,
  position = [0, 0, 0]
}: {
  size: [number, number, number];
  position?: [number, number, number];
}) => {
  const geometry = useMemo(() => new THREE.EdgesGeometry(new THREE.BoxGeometry(...size)), [size]);
  useEffect(() => () => geometry.dispose(), [geometry]);

  return (
    <lineSegments geometry={geometry} position={position}>
      <lineBasicMaterial color={OUTLINE} transparent opacity={0.85} />
    </lineSegments>
  );
};

/** Tilted rooftop solar array, straight off the blue line-art reference. */
const SolarArray = ({
  x,
  y,
  z,
  cols = 2,
  rows = 2
}: {
  x: number;
  y: number;
  z: number;
  cols?: number;
  rows?: number;
}) => (
  <group position={[x, y, z]}>
    {Array.from({ length: cols * rows }).map((_, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      return (
        <group key={i} position={[(col - (cols - 1) / 2) * 0.52, 0, (row - (rows - 1) / 2) * 0.46]}>
          <mesh position={[0, 0.12, 0]} rotation={[-0.42, 0, 0]} castShadow>
            <boxGeometry args={[0.46, 0.03, 0.38]} />
            <meshStandardMaterial color={SOLAR} roughness={0.35} metalness={0.1} />
          </mesh>
          <mesh position={[0, 0.05, 0.1]}>
            <boxGeometry args={[0.04, 0.1, 0.04]} />
            <meshStandardMaterial color={TRIM} roughness={0.8} />
          </mesh>
        </group>
      );
    })}
  </group>
);

/** Low-poly cloud drifting over the map. */
const Cloud = ({ x, y, z, scale = 1 }: { x: number; y: number; z: number; scale?: number }) => (
  <group position={[x, y, z]} scale={scale}>
    {[
      [0, 0, 0, 1],
      [1.1, -0.18, 0.2, 0.72],
      [-1, -0.14, -0.15, 0.62]
    ].map(([ox, oy, oz, s], i) => (
      <mesh key={i} position={[ox, oy, oz]} rotation={[i * 0.7, i * 1.2, 0]}>
        <icosahedronGeometry args={[s, 0]} />
        <meshStandardMaterial color="#ffffff" roughness={1} flatShading />
      </mesh>
    ))}
  </group>
);

/* ------------------------------------------------------------------ */
/* Street traffic                                                      */
/* ------------------------------------------------------------------ */

const Car = ({
  x,
  z,
  rotationY = 0,
  color = "#4c5cf0"
}: {
  x: number;
  z: number;
  rotationY?: number;
  color?: string;
}) => (
  <group position={[x, 0, z]} rotation={[0, rotationY, 0]}>
    <mesh position={[0, 0.24, 0]} castShadow>
      <boxGeometry args={[0.72, 0.3, 1.62]} />
      <meshStandardMaterial color={SHELL} roughness={0.6} />
    </mesh>
    <mesh position={[0, 0.5, -0.12]} castShadow>
      <boxGeometry args={[0.62, 0.26, 0.82]} />
      <meshStandardMaterial color={color} roughness={0.55} />
    </mesh>
    {[
      [-0.38, 0.55],
      [0.38, 0.55],
      [-0.38, -0.6],
      [0.38, -0.6]
    ].map(([wx, wz], i) => (
      <mesh key={i} position={[wx, 0.13, wz]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.13, 0.13, 0.09, 10]} />
        <meshStandardMaterial color="#8e9db8" roughness={0.7} />
      </mesh>
    ))}
  </group>
);

const Bus = ({ x, z, rotationY = 0 }: { x: number; z: number; rotationY?: number }) => (
  <group position={[x, 0, z]} rotation={[0, rotationY, 0]}>
    <mesh position={[0, 0.62, 0]} castShadow>
      <boxGeometry args={[0.94, 0.86, 3.1] } />
      <meshStandardMaterial color={SHELL} roughness={0.6} />
    </mesh>
    {/* window band + accent stripe */}
    <mesh position={[0, 0.82, 0]}>
      <boxGeometry args={[0.96, 0.3, 2.7]} />
      <meshStandardMaterial color={GLASS} roughness={0.35} />
    </mesh>
    <mesh position={[0, 0.36, 0]}>
      <boxGeometry args={[0.97, 0.12, 3.0]} />
      <meshStandardMaterial color="#4c5cf0" roughness={0.6} />
    </mesh>
    {[
      [-0.48, 1.06],
      [0.48, 1.06],
      [-0.48, -0.98],
      [0.48, -0.98]
    ].map(([wx, wz], i) => (
      <mesh key={i} position={[wx, 0.19, wz]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.19, 0.19, 0.1, 10]} />
        <meshStandardMaterial color="#8e9db8" roughness={0.7} />
      </mesh>
    ))}
  </group>
);

const Bench = ({ x, z, rotationY = 0 }: { x: number; z: number; rotationY?: number }) => (
  <group position={[x, 0, z]} rotation={[0, rotationY, 0]}>
    <mesh position={[0, 0.22, 0]} castShadow>
      <boxGeometry args={[0.9, 0.06, 0.28]} />
      <meshStandardMaterial color="#c3d0e6" roughness={0.85} />
    </mesh>
    <mesh position={[0, 0.38, -0.13]} castShadow>
      <boxGeometry args={[0.9, 0.26, 0.05]} />
      <meshStandardMaterial color="#c3d0e6" roughness={0.85} />
    </mesh>
  </group>
);

const Streetlamp = ({ x, z }: { x: number; z: number }) => (
  <group position={[x, 0, z]}>
    <mesh position={[0, 0.9, 0]} castShadow>
      <cylinderGeometry args={[0.04, 0.05, 1.8, 8]} />
      <meshStandardMaterial color={TRIM} roughness={0.6} />
    </mesh>
    <mesh position={[0, 1.84, 0]}>
      <sphereGeometry args={[0.09, 10, 10]} />
      <meshStandardMaterial
        color="#fff4d6"
        emissive="#ffe9a8"
        emissiveIntensity={0.6}
        roughness={0.4}
      />
    </mesh>
  </group>
);

const Figure = ({ x, z }: { x: number; z: number }) => (
  <group position={[x, 0, z]}>
    <mesh position={[0, 0.18, 0]}>
      <cylinderGeometry args={[0.08, 0.1, 0.36, 8]} />
      <meshStandardMaterial color={TRIM} roughness={0.6} />
    </mesh>
    <mesh position={[0, 0.44, 0]}>
      <sphereGeometry args={[0.09, 10, 10]} />
      <meshStandardMaterial color={TRIM} roughness={0.6} />
    </mesh>
  </group>
);

/* ------------------------------------------------------------------ */
/* Containers & cargo                                                  */
/* ------------------------------------------------------------------ */

const Container = ({
  position,
  color,
  rotationY = 0,
  scale = 1
}: {
  position: [number, number, number];
  color: string;
  rotationY?: number;
  scale?: number;
}) => (
  <group position={position} rotation={[0, rotationY, 0]} scale={scale}>
    <mesh castShadow receiveShadow>
      <boxGeometry args={[2.4, 0.95, 1.05]} />
      <meshStandardMaterial color={color} roughness={0.55} metalness={0.05} />
    </mesh>
    {/* corrugation ribs along the long faces */}
    {[-0.8, -0.27, 0.27, 0.8].map((ox) => (
      <mesh key={ox} position={[ox, 0, 0]}>
        <boxGeometry args={[0.07, 0.85, 1.09]} />
        <meshStandardMaterial color="#ffffff" transparent opacity={0.18} roughness={0.6} />
      </mesh>
    ))}
    {/* corner castings */}
    <mesh position={[0, 0.5, 0]}>
      <boxGeometry args={[2.42, 0.06, 1.07]} />
      <meshStandardMaterial color="#ffffff" transparent opacity={0.3} roughness={0.7} />
    </mesh>
  </group>
);

const ContainerStack = ({
  x,
  z,
  seed,
  cols = 3,
  rows = 3,
  maxHigh = 3
}: {
  x: number;
  z: number;
  seed: number;
  cols?: number;
  rows?: number;
  maxHigh?: number;
}) => {
  const boxes: React.ReactElement[] = [];
  for (let c = 0; c < cols; c++) {
    for (let r = 0; r < rows; r++) {
      const s = seed + c * 3.7 + r * 11.3;
      const high = 1 + Math.floor(rand(s) * maxHigh);
      for (let y = 0; y < high; y++) {
        boxes.push(
          <Container
            key={`${c}-${r}-${y}`}
            position={[x + c * 2.6, 0.5 + y * 0.99, z + r * 1.25]}
            color={ACCENTS[Math.floor(rand(s + y * 2.1) * ACCENTS.length)]}
          />
        );
      }
    }
  }
  return <group>{boxes}</group>;
};

/* ------------------------------------------------------------------ */
/* Port                                                                */
/* ------------------------------------------------------------------ */

const Water = () => (
  <group>
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[-52, 0.02, 0]} receiveShadow>
      <planeGeometry args={[60, 130]} />
      <meshStandardMaterial color={WATER} roughness={0.25} metalness={0.15} />
    </mesh>
    {/* quay wall */}
    <mesh position={[-22.4, 0.25, 0]}>
      <boxGeometry args={[1.2, 0.6, 130]} />
      <meshStandardMaterial color={SHELL_DIM} roughness={0.9} />
    </mesh>
  </group>
);

// Bollards + a fender line along the quay edge.
const QuayEdge = () => (
  <group>
    {Array.from({ length: 14 }).map((_, i) => (
      <mesh key={i} position={[-22.4, 0.72, -22 + i * 3.4]} castShadow>
        <cylinderGeometry args={[0.16, 0.2, 0.36, 10]} />
        <meshStandardMaterial color={TRIM} roughness={0.8} />
      </mesh>
    ))}
  </group>
);

const ContainerShip = ({ x, z }: { x: number; z: number }) => (
  <group position={[x, 0, z]}>
    {/* hull */}
    <mesh position={[0, 0.75, 0]} castShadow receiveShadow>
      <boxGeometry args={[5.2, 1.9, 20]} />
      <meshStandardMaterial color="#0b1c75" roughness={0.6} />
    </mesh>
    {/* bow: a diamond block whose forward corner forms the prow */}
    <mesh position={[0, 0.75, 10.7]} rotation={[0, Math.PI / 4, 0]} castShadow>
      <boxGeometry args={[3.68, 1.9, 3.68]} />
      <meshStandardMaterial color="#0b1c75" roughness={0.6} />
    </mesh>
    <mesh position={[0, 1.76, 10.7]} rotation={[0, Math.PI / 4, 0]}>
      <boxGeometry args={[3.6, 0.16, 3.6]} />
      <meshStandardMaterial color={SHELL_DIM} roughness={0.85} />
    </mesh>
    {/* waterline stripe */}
    <mesh position={[0, 0.2, 0]}>
      <boxGeometry args={[5.26, 0.28, 20.05]} />
      <meshStandardMaterial color="#3edcff" roughness={0.5} />
    </mesh>
    {/* deck */}
    <mesh position={[0, 1.76, 0]} receiveShadow>
      <boxGeometry args={[5.3, 0.16, 20.1]} />
      <meshStandardMaterial color={SHELL_DIM} roughness={0.85} />
    </mesh>
    {/* deck cargo */}
    {Array.from({ length: 6 }).map((_, r) =>
      Array.from({ length: 2 }).map((__, c) => {
        const high = 1 + Math.floor(rand(r * 4.3 + c * 9.1) * 3);
        return Array.from({ length: high }).map((___, y) => (
          <Container
            key={`${r}-${c}-${y}`}
            position={[-1.15 + c * 2.3, 2.35 + y * 0.99, -7 + r * 2.4]}
            color={ACCENTS[Math.floor(rand(r * 2.7 + c + y * 3.3) * ACCENTS.length)]}
            rotationY={Math.PI / 2}
            scale={0.92}
          />
        ));
      })
    )}
    {/* superstructure aft */}
    <mesh position={[0, 3.1, -8.6]} castShadow>
      <boxGeometry args={[4.4, 2.7, 2.6]} />
      <meshStandardMaterial color={SHELL} roughness={0.8} />
    </mesh>
    {[0, 1, 2].map((i) => (
      <mesh key={i} position={[0, 2.5 + i * 0.7, -7.28]}>
        <planeGeometry args={[3.6, 0.34]} />
        <meshStandardMaterial color={GLASS} roughness={0.4} />
      </mesh>
    ))}
    <mesh position={[0, 5.1, -8.6]} castShadow>
      <cylinderGeometry args={[0.5, 0.62, 1.3, 12]} />
      <meshStandardMaterial color="#4c5cf0" roughness={0.6} />
    </mesh>
  </group>
);

// Ship-to-shore gantry: portal legs on the quay, boom reaching out over
// the water, trolley + spreader hanging beneath.
const GantryCrane = ({ z, seed }: { z: number; seed: number }) => {
  const trolleyX = -26 + rand(seed) * 5;
  const legs = [-16, -21];
  const boomCenter = -24;
  const boomLength = 22;
  const boomY = 7.6;

  return (
    <group position={[0, 0, z]}>
      {[-1.9, 1.9].map((oz) =>
        legs.map((ox) => (
          <mesh key={`${oz}-${ox}`} position={[ox, 3.7, oz]} castShadow>
            <boxGeometry args={[0.34, 7.4, 0.34]} />
            <meshStandardMaterial color={SHELL} roughness={0.7} />
          </mesh>
        ))
      )}
      {/* portal cross-ties */}
      {legs.map((ox) => (
        <mesh key={ox} position={[ox, 5.2, 0]}>
          <boxGeometry args={[0.26, 0.26, 4.1]} />
          <meshStandardMaterial color={SHELL_DIM} roughness={0.8} />
        </mesh>
      ))}
      {/* twin boom girders reaching out over the ship */}
      {[-1.9, 1.9].map((oz) => (
        <mesh key={oz} position={[boomCenter, boomY, oz]} castShadow>
          <boxGeometry args={[boomLength, 0.45, 0.32]} />
          <meshStandardMaterial color="#4c5cf0" roughness={0.6} />
        </mesh>
      ))}
      {/* open lattice between the girders, not a solid deck */}
      {Array.from({ length: 9 }).map((_, i) => (
        <mesh
          key={i}
          position={[boomCenter - boomLength / 2 + 1.2 + i * 2.4, boomY, 0]}
        >
          <boxGeometry args={[0.18, 0.18, 3.9]} />
          <meshStandardMaterial color={SHELL_DIM} roughness={0.85} />
        </mesh>
      ))}
      {/* backreach A-frame */}
      <mesh position={[-18.5, 8.9, 0]} rotation={[0, 0, 0.35]}>
        <boxGeometry args={[0.24, 3.2, 0.24]} />
        <meshStandardMaterial color={SHELL_DIM} roughness={0.8} />
      </mesh>
      {/* machinery house */}
      <mesh position={[-17.4, 8.5, 0]} castShadow>
        <boxGeometry args={[2.4, 1.2, 3.4]} />
        <meshStandardMaterial color={SHELL} roughness={0.8} />
      </mesh>
      {/* trolley + hoist cables + a container on the spreader */}
      <mesh position={[trolleyX, 7.1, 0]} castShadow>
        <boxGeometry args={[1.3, 0.6, 2.6]} />
        <meshStandardMaterial color="#3edcff" roughness={0.5} />
      </mesh>
      {[-0.9, 0.9].map((oz) => (
        <mesh key={oz} position={[trolleyX, 5.5, oz]}>
          <cylinderGeometry args={[0.03, 0.03, 3.2, 6]} />
          <meshStandardMaterial color={TRIM} roughness={0.7} />
        </mesh>
      ))}
      <Container
        position={[trolleyX, 3.8, 0]}
        color={ACCENTS[Math.floor(rand(seed + 3) * ACCENTS.length)]}
        rotationY={Math.PI / 2}
      />
    </group>
  );
};

// Rubber-tyred yard crane straddling a container row.
const YardCrane = ({ x, z }: { x: number; z: number }) => (
  <group position={[x, 0, z]}>
    {[-3.2, 3.2].map((oz) =>
      [-1.6, 1.6].map((ox) => (
        <mesh key={`${oz}-${ox}`} position={[ox, 2.4, oz]} castShadow>
          <boxGeometry args={[0.3, 4.8, 0.3]} />
          <meshStandardMaterial color={SHELL} roughness={0.75} />
        </mesh>
      ))
    )}
    <mesh position={[0, 5.05, 0]} castShadow>
      <boxGeometry args={[0.5, 0.5, 7]} />
      <meshStandardMaterial color="#9d8cff" roughness={0.6} />
    </mesh>
    <mesh position={[0, 4.7, 1.2]}>
      <boxGeometry args={[1.2, 0.7, 1.4]} />
      <meshStandardMaterial color={GLASS} roughness={0.4} />
    </mesh>
  </group>
);

const PortTerminal = ({ x, z }: { x: number; z: number }) => {
  const glass = useWindowTexture(6, 3);
  return (
    <group position={[x, 0, z]}>
      <mesh position={[0, 1.6, 0]} castShadow receiveShadow>
        <boxGeometry args={[6.4, 3.2, 4]} />
        <meshStandardMaterial attach="material-0" map={glass} roughness={0.7} />
        <meshStandardMaterial attach="material-1" map={glass} roughness={0.7} />
        <meshStandardMaterial attach="material-2" color={ROOF} roughness={0.85} />
        <meshStandardMaterial attach="material-3" color={SHELL_DIM} roughness={0.9} />
        <meshStandardMaterial attach="material-4" map={glass} roughness={0.7} />
        <meshStandardMaterial attach="material-5" map={glass} roughness={0.7} />
      </mesh>
      <mesh position={[0, 3.32, 0]}>
        <boxGeometry args={[6.7, 0.22, 4.3]} />
        <meshStandardMaterial color="#4c5cf0" roughness={0.6} />
      </mesh>
      {/* control tower */}
      <mesh position={[3.6, 3.2, -1.2]} castShadow>
        <cylinderGeometry args={[0.7, 0.85, 6.4, 14]} />
        <meshStandardMaterial color={SHELL} roughness={0.8} />
      </mesh>
      <mesh position={[3.6, 6.7, -1.2]} castShadow>
        <cylinderGeometry args={[1.25, 1.05, 1.3, 14]} />
        <meshStandardMaterial color={GLASS} roughness={0.35} metalness={0.1} />
      </mesh>
      <mesh position={[3.6, 7.45, -1.2]}>
        <cylinderGeometry args={[1.3, 1.3, 0.16, 14]} />
        <meshStandardMaterial color={ROOF} roughness={0.85} />
      </mesh>
    </group>
  );
};

const PortGate = ({ x, z }: { x: number; z: number }) => (
  <group position={[x, 0, z]}>
    {[-2.2, 2.2].map((oz) => (
      <mesh key={oz} position={[0, 1.3, oz]} castShadow>
        <boxGeometry args={[0.9, 2.6, 0.9]} />
        <meshStandardMaterial color={SHELL} roughness={0.8} />
      </mesh>
    ))}
    <mesh position={[0, 3, 0]} castShadow>
      <boxGeometry args={[1.1, 0.8, 5.4]} />
      <meshStandardMaterial color="#4c5cf0" roughness={0.6} />
    </mesh>
  </group>
);

/* ------------------------------------------------------------------ */
/* Distribution warehouse                                              */
/* ------------------------------------------------------------------ */

const Pallet = ({ x, z, color }: { x: number; z: number; color: string }) => (
  <group position={[x, 0, z]}>
    <mesh position={[0, 0.08, 0]} receiveShadow>
      <boxGeometry args={[0.9, 0.16, 0.9]} />
      <meshStandardMaterial color={TRIM} roughness={0.9} />
    </mesh>
    <mesh position={[0, 0.48, 0]} castShadow>
      <boxGeometry args={[0.78, 0.66, 0.78]} />
      <meshStandardMaterial color={color} roughness={0.7} />
    </mesh>
  </group>
);

const Forklift = ({ x, z, rotationY = 0 }: { x: number; z: number; rotationY?: number }) => (
  <group position={[x, 0, z]} rotation={[0, rotationY, 0]}>
    <mesh position={[0, 0.4, 0]} castShadow>
      <boxGeometry args={[0.8, 0.6, 1.2]} />
      <meshStandardMaterial color="#3edcff" roughness={0.6} />
    </mesh>
    <mesh position={[0, 0.9, -0.2]} castShadow>
      <boxGeometry args={[0.7, 0.5, 0.6]} />
      <meshStandardMaterial color={SHELL} roughness={0.8} />
    </mesh>
    <mesh position={[0, 0.85, 0.62]}>
      <boxGeometry args={[0.6, 1.5, 0.1]} />
      <meshStandardMaterial color={TRIM} roughness={0.7} />
    </mesh>
    {[-0.3, 0.3].map((ox) => (
      <mesh key={ox} position={[ox, 0.12, 0.75]}>
        <boxGeometry args={[0.12, 0.06, 0.7]} />
        <meshStandardMaterial color={TRIM} roughness={0.7} />
      </mesh>
    ))}
  </group>
);

// The hub the truck delivers into: long shed, sawtooth skylights, a canopy
// over a row of loading docks facing the road, trailers backed in.
const DistributionHub = ({ x, z }: { x: number; z: number }) => {
  const W = 14;
  const D = 8;
  const H = 3.4;

  return (
    <group position={[x, 0, z]}>
      <mesh position={[0, H / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[W, H, D]} />
        <meshStandardMaterial color={SHELL} roughness={0.85} />
      </mesh>
      {/* brand band along the eave above the dock face */}
      <mesh position={[0, H - 0.32, D / 2 + 0.02]}>
        <planeGeometry args={[W * 0.94, 0.55]} />
        <meshStandardMaterial color="#4c5cf0" roughness={0.6} />
      </mesh>
      {/* sawtooth skylight roof */}
      {[-3, -2, -1, 0, 1, 2, 3].map((i) => (
        <group key={i} position={[i * 1.9, H + 0.02, 0]}>
          <mesh position={[0, 0.45, 0]} rotation={[0, 0, -0.5]} castShadow>
            <boxGeometry args={[1.5, 0.12, D * 0.94]} />
            <meshStandardMaterial color={ROOF} roughness={0.85} />
          </mesh>
          <mesh position={[0.62, 0.42, 0]}>
            <boxGeometry args={[0.1, 0.82, D * 0.9]} />
            <meshStandardMaterial color={GLASS} roughness={0.35} transparent opacity={0.85} />
          </mesh>
        </group>
      ))}
      {/* dock canopy on the road-facing (+z) side */}
      <mesh position={[0, 2.5, D / 2 + 1.1]} castShadow>
        <boxGeometry args={[W * 0.9, 0.18, 2.4]} />
        <meshStandardMaterial color={ROOF} roughness={0.85} />
      </mesh>
      {[-4.5, -1.5, 1.5, 4.5].map((ox) => (
        <mesh key={ox} position={[ox, 1.25, D / 2 + 2.2]}>
          <cylinderGeometry args={[0.09, 0.09, 2.5, 8]} />
          <meshStandardMaterial color={TRIM} roughness={0.7} />
        </mesh>
      ))}
      {/* dock doors + levellers */}
      {[-5, -3, -1, 1, 3, 5].map((ox) => (
        <group key={ox}>
          <mesh position={[ox * 1.1, 1, D / 2 + 0.02]}>
            <planeGeometry args={[1.5, 2]} />
            <meshStandardMaterial color={ox % 2 === 0 ? "#4c5cf0" : TRIM} roughness={0.7} />
          </mesh>
          <mesh position={[ox * 1.1, 0.3, D / 2 + 0.5]}>
            <boxGeometry args={[1.6, 0.6, 1]} />
            <meshStandardMaterial color={SHELL_DIM} roughness={0.9} />
          </mesh>
        </group>
      ))}
      {/* trailers backed into two of the docks */}
      <ParkedTrailer x={-3.3} z={D / 2 + 3.6} />
      <ParkedTrailer x={3.3} z={D / 2 + 3.6} />
      {/* yard activity in front of the docks */}
      <Forklift x={-6.6} z={D / 2 + 2.6} rotationY={-1.2} />
      <Pallet x={-5.6} z={D / 2 + 1.4} color="#9d8cff" />
      <Pallet x={-6.5} z={D / 2 + 0.6} color="#3edcff" />
      <Pallet x={6.2} z={D / 2 + 1.2} color="#4c5cf0" />
      <Figure x={-4.4} z={D / 2 + 1.9} />
      <Figure x={5.2} z={D / 2 + 2.4} />
      {/* container storage behind the shed */}
      <ContainerStack x={-4} z={-D / 2 - 2.6} seed={91} cols={3} rows={2} maxHigh={2} />
    </group>
  );
};

const ParkedTrailer = ({ x, z }: { x: number; z: number }) => (
  <group position={[x, 0, z]}>
    <mesh position={[0, 1.15, 0]} castShadow receiveShadow>
      <boxGeometry args={[2.2, 1.5, 4.6]} />
      <meshStandardMaterial color={SHELL} roughness={0.75} />
    </mesh>
    <mesh position={[0, 1.15, -2.32]}>
      <planeGeometry args={[1.9, 1.2]} />
      <meshStandardMaterial color={TRIM} roughness={0.8} />
    </mesh>
    <mesh position={[0, 0.36, 0]}>
      <boxGeometry args={[1.9, 0.2, 4.2]} />
      <meshStandardMaterial color={SHELL_DIM} roughness={0.85} />
    </mesh>
    {[-1.05, 1.05].map((ox) =>
      [-1.4, -0.8].map((oz) => (
        <mesh key={`${ox}-${oz}`} position={[ox, 0.26, oz]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.26, 0.26, 0.16, 12]} />
          <meshStandardMaterial color="#8f9bb3" roughness={0.8} />
        </mesh>
      ))
    )}
  </group>
);

/* ------------------------------------------------------------------ */
/* Customer district                                                   */
/* ------------------------------------------------------------------ */

const CustomerHouse = ({ x, z }: { x: number; z: number }) => (
  <group position={[x, 0, z]}>
    {/* garden plot */}
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.015, 0]} receiveShadow>
      <planeGeometry args={[9, 8]} />
      <meshStandardMaterial color="#dceee7" roughness={1} />
    </mesh>
    <mesh position={[0, 1.3, 0]} castShadow receiveShadow>
      <boxGeometry args={[4.4, 2.6, 3.6]} />
      <meshStandardMaterial color="#ffffff" roughness={0.85} />
    </mesh>
    <mesh position={[0, 3.05, 0]} rotation={[0, Math.PI / 4, 0]} castShadow>
      <coneGeometry args={[3.1, 1.5, 4]} />
      <meshStandardMaterial color="#4c5cf0" roughness={0.7} />
    </mesh>
    {/* door + windows facing the road (+z) */}
    <mesh position={[0, 0.75, 1.82]}>
      <planeGeometry args={[0.9, 1.5]} />
      <meshStandardMaterial color="#0b1c75" roughness={0.7} />
    </mesh>
    {[-1.4, 1.4].map((ox) => (
      <mesh key={ox} position={[ox, 1.6, 1.82]}>
        <planeGeometry args={[1, 0.9]} />
        <meshStandardMaterial color={GLASS} roughness={0.4} />
      </mesh>
    ))}
    {/* path to the kerb */}
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 3]} receiveShadow>
      <planeGeometry args={[1.1, 2.6]} />
      <meshStandardMaterial color={SHELL_DIM} roughness={0.95} />
    </mesh>
    {/* the customer, waiting at the door */}
    <Figure x={1.1} z={2.4} />
    {/* the parcel, on the step */}
    <mesh position={[-0.7, 0.22, 2.1]} castShadow>
      <boxGeometry args={[0.42, 0.42, 0.42]} />
      <meshStandardMaterial color="#9d8cff" roughness={0.7} />
    </mesh>
    <Tree x={-3.4} z={2.6} />
    <Tree x={3.6} z={-1.4} scale={0.85} />
  </group>
);

/* ------------------------------------------------------------------ */
/* Roads                                                               */
/* ------------------------------------------------------------------ */

// x1/z1 -> x2/z2, axis-aligned only.
const ROAD_SEGMENTS: Array<[number, number, number, number]> = [
  [-25, 10, -18, 10],
  [-18, 10, -18, -2],
  [-18, -2, -4, -2],
  [-4, -2, -4, 22],
  [-4, 22, 18, 22],
  [18, 22, 18, 6],
  [18, 6, 32, 6],
  [32, 6, 32, 18],
  [32, 18, 42, 18]
];

const ROAD_WIDTH = 3;

/** Road surface sits just above the district plates so junctions read cleanly. */
const ROAD_Y = PLATE_TOP + 0.02;

// Progress fraction at which the route finishes drawing. Past this point the
// scroll is spent entirely on the closing "drive-off" beat, so the route
// reveal and colour lerp are normalised against ROUTE_END rather than 1.
const ROUTE_END = 0.78;

// The straight lane the van turns onto once it leaves the customer's kerb.
// The van travels to DEPARTURE_LANE_VAN_STOP_Z, but the lane itself is drawn
// further than that (DEPARTURE_LANE_DRAW_END_Z) so there's always visible
// road ahead of the van — it never looks like it drives off the end of the
// world right as the beat finishes.
const DEPARTURE_LANE_X = 42;
const DEPARTURE_LANE_START_Z = 18;
const DEPARTURE_LANE_VAN_STOP_Z = 74;
const DEPARTURE_LANE_DRAW_END_Z = DEPARTURE_LANE_VAN_STOP_Z + 26;

const DepartureLane = () => {
  const length = DEPARTURE_LANE_DRAW_END_Z - DEPARTURE_LANE_START_Z;
  const cz = (DEPARTURE_LANE_START_Z + DEPARTURE_LANE_DRAW_END_Z) / 2;
  const dashCount = Math.max(1, Math.round(length / 2));
  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[DEPARTURE_LANE_X, ROAD_Y, cz]} receiveShadow>
        <planeGeometry args={[ROAD_WIDTH, length]} />
        <meshStandardMaterial color={ASPHALT} roughness={0.98} />
      </mesh>
      {Array.from({ length: dashCount }).map((_, i) => {
        const dz = DEPARTURE_LANE_START_Z + (i + 0.5) * (length / dashCount);
        return (
          <mesh key={i} rotation={[-Math.PI / 2, 0, 0]} position={[DEPARTURE_LANE_X, ROAD_Y + 0.005, dz]}>
            <planeGeometry args={[0.12, 0.9]} />
            <meshStandardMaterial color="#ffffff" roughness={0.9} />
          </mesh>
        );
      })}
    </group>
  );
};

/**
 * Zebra crossings, placed mid-block rather than on the corners. `axis` is the
 * direction the road runs, so the stripes are drawn along it.
 */
const CROSSWALKS: Array<[number, number, "x" | "z"]> = [
  [-18, 6, "z"],
  [-10, -2, "x"],
  [-4, 10, "z"],
  [8, 22, "x"],
  [18, 14, "z"],
  [26, 6, "x"]
];

const Crosswalk = ({ x, z, axis }: { x: number; z: number; axis: "x" | "z" }) => (
  <group position={[x, ROAD_Y + 0.008, z]}>
    {[-1.05, -0.63, -0.21, 0.21, 0.63, 1.05].map((offset) => (
      <mesh
        key={offset}
        rotation={[-Math.PI / 2, 0, 0]}
        position={axis === "x" ? [offset, 0, 0] : [0, 0, offset]}
      >
        <planeGeometry args={axis === "x" ? [0.24, 1.5] : [1.5, 0.24]} />
        <meshStandardMaterial color="#ffffff" roughness={0.9} />
      </mesh>
    ))}
  </group>
);

const Roads = () => (
  <group>
    {ROAD_SEGMENTS.map(([x1, z1, x2, z2], i) => {
      const cx = (x1 + x2) / 2;
      const cz = (z1 + z2) / 2;
      const lengthX = Math.abs(x2 - x1);
      const lengthZ = Math.abs(z2 - z1);
      const w = lengthX > 0 ? lengthX + ROAD_WIDTH : ROAD_WIDTH;
      const d = lengthZ > 0 ? lengthZ + ROAD_WIDTH : ROAD_WIDTH;
      const dashCount = Math.max(1, Math.round(Math.max(lengthX, lengthZ) / 2));
      return (
        <group key={i}>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[cx, ROAD_Y, cz]} receiveShadow>
            <planeGeometry args={[w, d]} />
            <meshStandardMaterial color={ASPHALT} roughness={0.98} />
          </mesh>
          {Array.from({ length: dashCount }).map((_, dIdx) => {
            const t = (dIdx + 0.5) / dashCount;
            const dx = x1 + (x2 - x1) * t;
            const dz = z1 + (z2 - z1) * t;
            return (
              <mesh
                key={dIdx}
                rotation={[-Math.PI / 2, 0, 0]}
                position={[dx, ROAD_Y + 0.005, dz]}
              >
                <planeGeometry
                  args={lengthX > 0 ? [0.9, 0.12] : [0.12, 0.9]}
                />
                <meshStandardMaterial color="#ffffff" roughness={0.9} />
              </mesh>
            );
          })}
        </group>
      );
    })}
    {CROSSWALKS.map(([x, z, axis], i) => (
      <Crosswalk key={i} x={x} z={z} axis={axis} />
    ))}
  </group>
);

/* ------------------------------------------------------------------ */
/* Ground                                                              */
/* ------------------------------------------------------------------ */

const Ground = () => (
  <group>
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]} receiveShadow>
      <planeGeometry args={[220, 220]} />
      <meshStandardMaterial color="#e7edf7" roughness={1} />
    </mesh>
    {/* port apron: a slightly darker hardstanding around the quay */}
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[-16, 0.005, 0]} receiveShadow>
      <planeGeometry args={[14, 46]} />
      <meshStandardMaterial color="#dfe6f2" roughness={1} />
    </mesh>
  </group>
);

/* ------------------------------------------------------------------ */
/* The travelling vehicle                                              */
/* ------------------------------------------------------------------ */

// Semi-truck: cab + box trailer. Modelled facing +Z.
const SemiTruck = React.forwardRef<THREE.Group>((_, ref) => (
  <group ref={ref}>
    {/* trailer */}
    <mesh position={[0, 0.95, -1.5]} castShadow>
      <boxGeometry args={[1.15, 1.2, 2.9]} />
      <meshStandardMaterial color={SHELL} roughness={0.7} />
    </mesh>
    <mesh position={[0, 1.28, -1.5]}>
      <boxGeometry args={[1.18, 0.34, 2.6]} />
      <meshStandardMaterial color="#4c5cf0" roughness={0.6} />
    </mesh>
    {/* cab */}
    <mesh position={[0, 0.78, 0.55]} castShadow>
      <boxGeometry args={[1.1, 0.9, 1.2]} />
      <meshStandardMaterial color="#0b1c75" roughness={0.6} />
    </mesh>
    <mesh position={[0, 0.82, 1.16]}>
      <planeGeometry args={[0.85, 0.5]} />
      <meshStandardMaterial color={GLASS} roughness={0.35} />
    </mesh>
    <mesh position={[0, 0.28, 0.9]}>
      <boxGeometry args={[1.12, 0.4, 0.6]} />
      <meshStandardMaterial color="#3edcff" roughness={0.55} />
    </mesh>
    {/* wheels */}
    {[-0.58, 0.58].map((ox) =>
      [1.0, 0.2, -1.9, -2.5].map((oz) => (
        <mesh key={`${ox}-${oz}`} position={[ox, 0.27, oz]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.27, 0.27, 0.16, 12]} />
          <meshStandardMaterial color="#7c88a3" roughness={0.85} />
        </mesh>
      ))
    )}
  </group>
));
SemiTruck.displayName = "SemiTruck";

// Last-mile van, used after the hub hand-off. Modelled facing +Z.
const DeliveryVan = React.forwardRef<THREE.Group>((_, ref) => (
  <group ref={ref}>
    <mesh position={[0, 0.72, -0.35]} castShadow>
      <boxGeometry args={[1, 1.05, 1.9]} />
      <meshStandardMaterial color={SHELL} roughness={0.7} />
    </mesh>
    <mesh position={[0, 1.05, -0.35]}>
      <boxGeometry args={[1.03, 0.3, 1.7]} />
      <meshStandardMaterial color="#9d8cff" roughness={0.6} />
    </mesh>
    <mesh position={[0, 0.6, 0.85]} castShadow>
      <boxGeometry args={[0.98, 0.8, 0.7]} />
      <meshStandardMaterial color="#4c5cf0" roughness={0.6} />
    </mesh>
    <mesh position={[0, 0.68, 1.21]}>
      <planeGeometry args={[0.8, 0.44]} />
      <meshStandardMaterial color={GLASS} roughness={0.35} />
    </mesh>
    {[-0.52, 0.52].map((ox) =>
      [0.75, -0.95].map((oz) => (
        <mesh key={`${ox}-${oz}`} position={[ox, 0.24, oz]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.24, 0.24, 0.14, 12]} />
          <meshStandardMaterial color="#7c88a3" roughness={0.85} />
        </mesh>
      ))
    )}
  </group>
));
DeliveryVan.displayName = "DeliveryVan";

/* ------------------------------------------------------------------ */
/* Route + vehicle animation                                           */
/* ------------------------------------------------------------------ */

// Progress at which the truck reaches the distribution hub and the load is
// handed over to the last-mile van.
const HUB_PROGRESS = 0.51;

const RoutePath = ({
  progressRef,
  colorStops
}: {
  progressRef: React.MutableRefObject<number>;
  colorStops: string[];
}) => {
  const { size } = useThree();

  // The route the freight actually takes: out of the port gate, across the
  // city, into the distribution hub, then out to the customer's street.
  const ROUTE_WAYPOINTS = useMemo(
    () =>
      ROAD_SEGMENTS.reduce<THREE.Vector3[]>((acc, [x1, z1, x2, z2], i) => {
        if (i === 0) acc.push(new THREE.Vector3(x1, 0.35, z1));
        acc.push(new THREE.Vector3(x2, 0.35, z2));
        return acc;
      }, []),
    []
  );

  const fullPoints = useMemo(() => {
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i < ROUTE_WAYPOINTS.length - 1; i++) {
      const a = ROUTE_WAYPOINTS[i];
      const b = ROUTE_WAYPOINTS[i + 1];
      const segs = Math.max(6, Math.round(a.distanceTo(b) * 3));
      for (let s = 0; s < segs; s++) {
        pts.push(a.clone().lerp(b, s / segs));
      }
    }
    pts.push(ROUTE_WAYPOINTS[ROUTE_WAYPOINTS.length - 1]);
    return pts;
  }, [ROUTE_WAYPOINTS]);

  const colorObjs = useMemo(() => colorStops.map((c) => new THREE.Color(c)), [colorStops]);

  const makeLine = (linewidth: number, opacity: number) => {
    const material = new LineMaterial({
      color: new THREE.Color(colorStops[0]).getHex(),
      linewidth,
      resolution: new THREE.Vector2(size.width, size.height),
      transparent: true,
      opacity,
      depthTest: false,
      depthWrite: false
    });
    const geometry = new LineGeometry();
    const start = fullPoints.slice(0, 2).flatMap((p) => [p.x, p.y, p.z]);
    geometry.setPositions(start);
    const line = new Line2(geometry, material);
    line.computeLineDistances();
    line.renderOrder = 999;
    return line;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const glowLine = useMemo(() => makeLine(13, 0.2), [colorStops, fullPoints, size.width, size.height]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const coreLine = useMemo(() => makeLine(4, 0.9), [colorStops, fullPoints, size.width, size.height]);

  const baseLine = useMemo(() => {
    const material = new LineMaterial({
      color: new THREE.Color("#9fc3ff").getHex(),
      linewidth: 2.5,
      resolution: new THREE.Vector2(size.width, size.height),
      transparent: true,
      opacity: 0.3,
      depthTest: false,
      depthWrite: false
    });
    const geometry = new LineGeometry();
    geometry.setPositions(fullPoints.flatMap((p) => [p.x, p.y, p.z]));
    const line = new Line2(geometry, material);
    line.computeLineDistances();
    line.renderOrder = 997;
    return line;
  }, [fullPoints, size.width, size.height]);

  const truckRef = useRef<THREE.Group>(null);
  const vanRef = useRef<THREE.Group>(null);
  const headingRef = useRef(0);

  useFrame(() => {
    const p = progressRef.current;
    // Route reveal / colour lerp run against routeP, not raw progress, so the
    // route finishes drawing exactly at ROUTE_END and the closing beat
    // (ROUTE_END -> 1) has nothing left to animate in.
    const routeP = Math.min(1, p / ROUTE_END);
    const visibleCount = Math.max(2, Math.floor(fullPoints.length * routeP));
    const points = fullPoints.slice(0, visibleCount);
    const flat = points.flatMap((pt) => [pt.x, pt.y, pt.z]);

    [glowLine, coreLine].forEach((line) => {
      (line.geometry as LineGeometry).setPositions(flat);
      line.computeLineDistances();
    });

    const stopIdx = routeP * (colorObjs.length - 1);
    const i0 = Math.min(colorObjs.length - 2, Math.floor(stopIdx));
    const t = stopIdx - i0;
    const c = colorObjs[i0].clone().lerp(colorObjs[i0 + 1], t);
    (glowLine.material as LineMaterial).color = c;
    (coreLine.material as LineMaterial).color = c;

    const tip = points[points.length - 1] ?? fullPoints[0];
    const prev = points[points.length - 2] ?? fullPoints[0];

    const inBeat = p > ROUTE_END;
    const du = inBeat ? (p - ROUTE_END) / (1 - ROUTE_END) : 0;

    let targetHeading: number | null = null;
    if (inBeat) {
      // Squared up, facing straight down the departure lane. The turn out of
      // the driveway is carried by this settling toward 0 over the beat.
      targetHeading = 0;
    } else {
      const dx = tip.x - prev.x;
      const dz = tip.z - prev.z;
      if (dx !== 0 || dz !== 0) targetHeading = Math.atan2(dx, dz);
    }
    if (targetHeading !== null) {
      // shortest-arc damping so 180° turns don't spin the long way round
      let delta = targetHeading - headingRef.current;
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      headingRef.current += delta * 0.18;
    }

    // Before the hub the semi carries the container; after it, the van
    // takes the last mile.
    const truckLeg = p < HUB_PROGRESS;
    // The van stays parked at the kerb once the route completes, so the
    // final frame reads as "delivered" rather than "vehicle vanished".
    const rolling = p > 0.005;

    if (truckRef.current) {
      truckRef.current.visible = rolling && truckLeg;
      truckRef.current.position.set(tip.x, ROAD_Y, tip.z);
      truckRef.current.rotation.y = headingRef.current;
    }
    if (vanRef.current) {
      vanRef.current.visible = rolling && !truckLeg;
      if (inBeat) {
        const ease = du * du * (3 - 2 * du);
        const vanZ =
          DEPARTURE_LANE_START_Z + (DEPARTURE_LANE_VAN_STOP_Z - DEPARTURE_LANE_START_Z) * ease;
        vanRef.current.position.set(DEPARTURE_LANE_X, ROAD_Y, vanZ);
      } else {
        vanRef.current.position.set(tip.x, ROAD_Y, tip.z);
      }
      vanRef.current.rotation.y = headingRef.current;
    }
  });

  useEffect(() => {
    return () => {
      [glowLine, coreLine, baseLine].forEach((line) => {
        line.geometry.dispose();
        (line.material as LineMaterial).dispose();
      });
    };
  }, [glowLine, coreLine, baseLine]);

  return (
    <>
      <primitive object={baseLine} />
      <primitive object={glowLine} />
      <primitive object={coreLine} />
      <SemiTruck ref={truckRef} />
      <DeliveryVan ref={vanRef} />
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Camera                                                              */
/* ------------------------------------------------------------------ */

const CameraRig = ({
  progressRef,
  waypoints,
  breakpoints
}: {
  progressRef: React.MutableRefObject<number>;
  waypoints: CameraWaypoint[];
  breakpoints: number[];
}) => {
  const { camera } = useThree();
  const orthoCamera = camera as THREE.OrthographicCamera;
  const tmpPos = useMemo(() => new THREE.Vector3(), []);
  const tmpLook = useMemo(() => new THREE.Vector3(), []);
  const currentLook = useRef(new THREE.Vector3(...waypoints[0].lookAt));
  const currentZoom = useRef(waypoints[0].zoom ?? 26);

  useFrame(() => {
    const p = progressRef.current;
    const i0 = findSegment(breakpoints, p);
    const segStart = breakpoints[i0];
    const segEnd = breakpoints[i0 + 1];
    const t = segEnd > segStart ? (p - segStart) / (segEnd - segStart) : 0;
    const a = waypoints[i0];
    const b = waypoints[i0 + 1];

    tmpPos.set(...a.position).lerp(tmpPos.clone().set(...b.position), t);
    tmpLook.set(...a.lookAt).lerp(tmpLook.clone().set(...b.lookAt), t);
    const targetZoom = THREE.MathUtils.lerp(a.zoom ?? 26, b.zoom ?? 26, t);

    // Van-locked pan for the closing drive-off beat: interpolating the
    // waypoints alone lets the van outrun the frame, so once we're in the
    // last segment we override the z of both position and lookAt to track
    // the van's position along the departure lane. Blended in over the first
    // fifth of the beat so there's no lurch when the override kicks in.
    if (i0 === waypoints.length - 2 && p > ROUTE_END) {
      const du = Math.min(1, (p - ROUTE_END) / (1 - ROUTE_END));
      const ease = du * du * (3 - 2 * du);
      const vanZ =
        DEPARTURE_LANE_START_Z + (DEPARTURE_LANE_VAN_STOP_Z - DEPARTURE_LANE_START_Z) * ease;
      const k = Math.min(1, du / 0.2);
      tmpLook.z += (vanZ - 1.5 - tmpLook.z) * k;
      tmpPos.z += (vanZ + (a.position[2] - a.lookAt[2]) - 1.5 - tmpPos.z) * k;
    }

    camera.position.lerp(tmpPos, 0.08);
    currentLook.current.lerp(tmpLook, 0.08);
    camera.lookAt(currentLook.current);

    currentZoom.current = THREE.MathUtils.lerp(currentZoom.current, targetZoom, 0.08);
    if (orthoCamera.zoom !== undefined) {
      orthoCamera.zoom = currentZoom.current;
      orthoCamera.updateProjectionMatrix();
    }
  });

  return null;
};

/* ------------------------------------------------------------------ */
/* Scene                                                               */
/* ------------------------------------------------------------------ */

const Turbine = ({ x, z }: { x: number; z: number }) => {
  const bladeRef = useRef<THREE.Group>(null);
  useFrame((_, delta) => {
    if (bladeRef.current) bladeRef.current.rotation.z += delta * 0.6;
  });

  return (
    <group position={[x, 0, z]}>
      <mesh position={[0, 2, 0]} castShadow>
        <cylinderGeometry args={[0.06, 0.09, 4, 8]} />
        <meshStandardMaterial color="#eef2f8" roughness={0.7} />
      </mesh>
      <group ref={bladeRef} position={[0, 4, 0]}>
        {[0, 1, 2].map((i) => (
          <mesh key={i} rotation={[0, 0, (i * Math.PI * 2) / 3]} position={[0, 0.5, 0]}>
            <boxGeometry args={[0.08, 1, 0.03]} />
            <meshStandardMaterial color="#dbe3ef" roughness={0.7} />
          </mesh>
        ))}
      </group>
    </group>
  );
};

const Scene = ({ progressRef, waypoints, breakpoints, routeColorStops }: CitySceneProps) => {
  return (
    <>
      <color attach="background" args={["#e9eef7"]} />
      <fog attach="fog" args={["#e9eef7", 55, 150]} />
      <hemisphereLight args={["#ffffff", "#c7d4ea", 1.35]} />
      <ambientLight intensity={0.5} />
      <directionalLight
        position={[16, 24, 10]}
        intensity={1.35}
        color="#ffffff"
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-camera-left={-60}
        shadow-camera-right={60}
        shadow-camera-top={60}
        shadow-camera-bottom={-60}
        shadow-camera-far={140}
      />

      <CameraRig progressRef={progressRef} waypoints={waypoints} breakpoints={breakpoints} />
      <Ground />
      <Water />
      <QuayEdge />
      <Roads />
      <DepartureLane />

      {/* --- 1. the port: ship alongside, cranes working, yard stacked --- */}
      <ContainerShip x={-27} z={0} />
      <GantryCrane z={-6} seed={3} />
      <GantryCrane z={7} seed={17} />
      <ContainerStack x={-21} z={-18} seed={5} cols={3} rows={5} maxHigh={3} />
      <ContainerStack x={-21} z={-8} seed={29} cols={3} rows={4} maxHigh={3} />
      <YardCrane x={-17.5} z={-14} />
      <PortTerminal x={-13} z={17} />
      <PortGate x={-24} z={10} />
      <Figure x={-19} z={4} />
      <Figure x={-16} z={-8} />

      {/* --- 2. the city the freight crosses --- */}
      {CLUSTER_CENTERS.map(([x, z], i) => (
        <DistrictPlate key={i} x={x} z={z} seed={30 + i * 13} />
      ))}
      {BUILDING_CLUSTERS.map((b, i) => (
        <BuildingMesh key={i} b={b} />
      ))}
      {/* everything below stands on the plates, hence the lift */}
      <group position={[0, PLATE_TOP, 0]}>
        {TREES.map(([x, z], i) => (
          <Tree key={i} x={x} z={z} seed={i * 3.1} />
        ))}
        {LAMPS.map(([x, z], i) => (
          <Streetlamp key={i} x={x} z={z} />
        ))}
        {BENCHES.map(([x, z], i) => (
          <Bench key={i} x={x} z={z} rotationY={i * 1.1} />
        ))}
      </group>
      {TURBINES.map(([x, z], i) => (
        <Turbine key={i} x={x} z={z} />
      ))}

      {/* traffic sharing the road with the freight */}
      <group position={[0, ROAD_Y, 0]}>
        <Car x={-18.7} z={2.4} rotationY={0} color="#9d8cff" />
        <Car x={-9} z={-1.3} rotationY={Math.PI / 2} color="#4c5cf0" />
        <Car x={-3.3} z={16.5} rotationY={Math.PI} color="#3edcff" />
        <Car x={13} z={22.7} rotationY={Math.PI / 2} color="#6f7dff" />
        <Car x={30} z={5.3} rotationY={-Math.PI / 2} color="#9d8cff" />
        <Bus x={-4.7} z={5} rotationY={0} />
        <Bus x={22} z={6.7} rotationY={Math.PI / 2} />
      </group>

      {/* low-poly clouds drifting over the map */}
      <Cloud x={-14} y={17} z={-24} scale={1.5} />
      <Cloud x={20} y={20} z={-30} scale={1.9} />
      <Cloud x={40} y={16} z={-20} scale={1.2} />

      {/* --- 3. the distribution hub the truck unloads into --- */}
      <DistributionHub x={6} z={13} />

      {/* --- 4. the customer's door --- */}
      <CustomerHouse x={42} z={13} />

      <RoutePath progressRef={progressRef} colorStops={routeColorStops} />
    </>
  );
};

const CityScene = ({ progressRef, waypoints, breakpoints, routeColorStops }: CitySceneProps) => {
  return (
    <Canvas
      shadows
      flat
      orthographic
      dpr={[1, 1.75]}
      camera={{ position: waypoints[0].position, zoom: waypoints[0].zoom ?? 26, near: 0.1, far: 400 }}
      gl={{ antialias: true }}
      style={{ width: "100%", height: "100%" }}
    >
      <Scene
        progressRef={progressRef}
        waypoints={waypoints}
        breakpoints={breakpoints}
        routeColorStops={routeColorStops}
      />
    </Canvas>
  );
};

export default CityScene;
